import * as linkify from 'linkifyjs'
import Vue from 'vue'

Vue.directive('focus', {
    inserted: function (el) {
        el.focus()
    },
    update: function (el) {
        Vue.nextTick(function () {
            el.focus()
        })
    }
})

Vue.directive('linkified', {
    bind: function (el, binding) {
        updateLinkified(el, binding)
    },
    update: function (el, binding) {
        updateLinkified(el, binding)
    },
    componentUpdated: function (el, binding) {
        updateLinkified(el, binding)
    }
})

function updateLinkified(el, binding) {
    const options = binding.value?.options || binding.value || {}
    const target = options.target || '_blank'
    
    // Check if element already has HTML content (from v-html)
    const hasHtmlContent = el.innerHTML !== el.textContent
    
    if (hasHtmlContent) {
        // Element has HTML structure, process text nodes while preserving HTML
        processHtmlContent(el, target)
    } else {
        // Plain text content, process normally
        processPlainText(el, target)
    }
}

function processPlainText(el, target) {
    // Get the text content
    const originalText = el.textContent || el.innerText || ''
    
    if (!originalText.trim()) {
        return
    }
    
    // Find all links in the text
    const links = linkify.find(originalText)
    
    if (links.length === 0) {
        return
    }
    
    // Build HTML by iterating through text and inserting links at correct positions
    let html = ''
    let lastIndex = 0
    
    // Sort links by start position
    const sortedLinks = [...links].sort((a, b) => a.start - b.start)
    
    sortedLinks.forEach(link => {
        // Add text before the link
        const beforeText = originalText.substring(lastIndex, link.start)
        html += escapeHtml(beforeText)
        
        // Add the link
        const linkText = originalText.substring(link.start, link.end)
        const href = link.href || link.value
        const escapedLinkText = escapeHtml(linkText)
        html += `<a href="${href}" target="${target}" rel="noopener noreferrer">${escapedLinkText}</a>`
        
        lastIndex = link.end
    })
    
    // Add remaining text after last link
    if (lastIndex < originalText.length) {
        html += escapeHtml(originalText.substring(lastIndex))
    }
    
    // Update the element's innerHTML
    el.innerHTML = html
}

function processHtmlContent(el, target) {
    // Clone the element to work with
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = el.innerHTML
    
    // Get all text nodes and process them
    const walker = document.createTreeWalker(
        tempDiv,
        NodeFilter.SHOW_TEXT,
        null,
        false
    )
    
    // Collect all text nodes first (to avoid modifying tree while walking)
    const textNodes = []
    let node = walker.nextNode()
    while (node) {
        if (node.textContent.trim()) {
            textNodes.push({
                node: node,
                text: node.textContent
            })
        }
        node = walker.nextNode()
    }
    
    // Process each text node
    textNodes.forEach(({ node: textNode, text }) => {
        const links = linkify.find(text)
        
        if (links.length === 0) {
            return
        }
        
        // Build HTML with links
        let html = ''
        let lastIndex = 0
        
        const sortedLinks = [...links].sort((a, b) => a.start - b.start)
        
        sortedLinks.forEach(link => {
            // Add text before the link
            const beforeText = text.substring(lastIndex, link.start)
            html += escapeHtml(beforeText)
            
            // Add the link
            const linkText = text.substring(link.start, link.end)
            const href = link.href || link.value
            const escapedLinkText = escapeHtml(linkText)
            html += `<a href="${href}" target="${target}" rel="noopener noreferrer">${escapedLinkText}</a>`
            
            lastIndex = link.end
        })
        
        // Add remaining text after last link
        if (lastIndex < text.length) {
            html += escapeHtml(text.substring(lastIndex))
        }
        
        // Replace text node with HTML
        const tempSpan = document.createElement('span')
        tempSpan.innerHTML = html
        
        // Replace the text node with the new content
        const fragment = document.createDocumentFragment()
        while (tempSpan.firstChild) {
            fragment.appendChild(tempSpan.firstChild)
        }
        
        textNode.parentNode.replaceChild(fragment, textNode)
    })
    
    // Update the original element with processed content
    el.innerHTML = tempDiv.innerHTML
}

function escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
}

// This variable will hold the reference to
// document's click handler
let handleOutsideClick, 
    handleMouseMove, 
    handleMouseDown,
    handleMouseUp, 
    moved = false,
    clickedAndDragged = false

Vue.directive('closable', {
  bind (el, binding, vnode) {
    handleMouseMove = () => {
      moved = true
    }

    handleMouseDown = () => {
      moved = false
    }

    handleMouseUp = () => {
      if (moved) {
        clickedAndDragged = true
      }
    }

    // Here's the click/touchstart handler
    // (it is registered below)
    handleOutsideClick = (e) => {
      e.stopPropagation()
      // Get the handler method name and the exclude array
      // from the object used in v-closable
      const { handler, exclude = [] } = binding.value

      // This variable indicates if the clicked element is excluded
      let clickedOnExcludedEl = false
      exclude.forEach(refName => {
        // We only run this code if we haven't detected
        // any excluded element yet
        if (!clickedOnExcludedEl) {
          // Get the element using the reference name
          const excludedEl = vnode.context.$refs[refName]
          
          // See if this excluded element
          // is the same element the user just clicked on
          if (excludedEl) {
            clickedOnExcludedEl = excludedEl.contains(e.target)
          }
        }
      })

      // check if clicked element has these classes
      // these are for elements that you cannot set refs to
      const { classList } = e.target

      if (classList && (
        classList.contains('confirm-transfer-button') || 
        classList.contains('cancel-transfer-button') || 
        classList.contains('el-message-box__close') || 
        classList.contains('el-icon-close')
      )) {
        clickedOnExcludedEl = true
      }

      // We check to see if the clicked element is not
      // the dialog element and not excluded
      if (!el.contains(e.target) && !clickedOnExcludedEl && !clickedAndDragged) {
        // If the clicked element is outside the dialog
        // and not the button, then call the outside-click handler
        // from the same component this directive is used in
        vnode.context[handler]()
      }

      clickedAndDragged = false
    }
    // Register click/touchstart event listeners on the whole page
    document.addEventListener('click', handleOutsideClick)
    document.addEventListener('touchstart', handleOutsideClick)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('mouseup', handleMouseUp)
  },

  unbind () {
    // If the element that has v-closable is removed, then
    // unbind click/touchstart listeners from the whole page
    document.removeEventListener('click', handleOutsideClick)
    document.removeEventListener('touchstart', handleOutsideClick)
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mousedown', handleMouseDown)
    document.removeEventListener('mouseup', handleMouseUp)
  }
})

Vue.directive('click-outside', {
  bind (el, binding, vnode) {
    el.clickOutsideEvent = function (event) {
      // here I check that click was outside the el and his children
      if (!(el == event.target || el.contains(event.target))) {
        // and if it did, call method provided in attribute value
        vnode.context[binding.expression](event);
      }
    };
    document.body.addEventListener('click', el.clickOutsideEvent)
  },
  unbind (el) {
    document.body.removeEventListener('click', el.clickOutsideEvent)
  },
});